import { Box, Card, CardHeader, Divider, IconButton, Link, Tooltip, Typography } from "@mui/material";
import { crudGetList, useListContext, useRecordContext, useVersion } from "ra-core";
import DownloadIcon from '@mui/icons-material/Download';
import { FC } from "react";
import { RecPatch } from "@/dataProvider/tools";
import { BackupResponseDto, BackupStatus, DatabaseResponseDto } from "@/gen/api/backup";
import FunctionField from "../field/FunctionField";
import TextField from "../field/TextField";
import { Datagrid } from "../list/datagrid";
import Scrollbar from "../Scrollbar";
import BackupEventsTimeLine from "./BackupEventsTimeLine";
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { useLogViewerContext } from "./LogViewer";
import BackupTypeField from "./BackupTypeField";
import { merge } from "lodash";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import retrieveAxiosErr from "@/utils/retrieveAxiosErr";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_BACKUP_JOBS } from "@/constants";


const BackupRecordsTable: FC = () => {
  const hasWritePermit = useHasPermit([MANAGE_BACKUP_JOBS])
  const database = useRecordContext<DatabaseResponseDto>()
  const listContext = useListContext()
  useVersion()
  const { showLogInViewer } = useLogViewerContext()

  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const onDownloadBackupConfigFile = (x: number) => 
    dispatch(merge(
      crudGetList('download-config-file', pages(), sorts(), { dbId: database?.databaseId || 0, backupId: x }),
      {
        onFailure: (err: any) => {
          const errMsg = retrieveAxiosErr(err)
          enqueueSnackbar(`Download failed: ${errMsg}`, { variant: 'error' })
        }
      },
    ))
  
  return (
    <Card>
      <CardHeader title="Backups" />
      <Divider />
      <Scrollbar options={{ suppressScrollY: true }}>
        <Box sx={{ minWidth: 1150 }}>

          <Datagrid 
            hasBulkActions={false} 
            {...listContext}
            sx={{ 
              '& th' : { py: 2 },
              '& td' : { pb: 2 },
              '& td.column-backupTypeText' : { position: 'relative' },
            }}
          >
            <BackupTypeField source="backupTypeText" label="Backup Type" />
            <FunctionField  
              label="Progress" 
              render={(rec?: BackupResponseDto & RecPatch) => (
                <BackupEventsTimeLine backupRec={rec} sx={{ minWidth: 400, mt: -3 }} />
              )}
              sortable={false}
            />
            <FunctionField 
              source="engineVersion" 
              label="Engine Version" 
              sortable={false} 
              render={(rec?: BackupResponseDto & RecPatch) =>(
                <>
                  <Typography variant="body2">{rec?.engineVersion}</Typography>
                  <Typography variant="caption">{rec?.engineEdition}</Typography>
                </>
              )}
            />
            <TextField source="sizeText" label="File Size" sortable={false} />
            <FunctionField 
              label="Validation" 
              sortable={false} 
              render={(rec?: BackupResponseDto & RecPatch) => {
                if (!rec) return null
                const bag = getStatusRelated(rec)
                return (
                  <Box sx={{ display: 'flex', alignContent: 'center'}}>
                    <Box 
                      component="span" 
                      sx={{ 
                        bgcolor: bag.color, 
                        width: 24, 
                        height: 24, 
                        display: 'inline-block',
                        borderRadius: '50%',
                        mr: 1,
                      }} 
                    />
                    {bag.isFailed ?
                      <Link
                        component="button"
                        color="secondary"
                        underline="hover"
                        onClick={(e:any) => showLogInViewer(e, bag.errLogContent || '')}
                        sx={{ cursor: 'pointer' }}
                      >
                        <Typography
                          component="span"
                          variant="caption"
                        >
                          (View Log)
                        </Typography>
                      </Link> 
                      : null}
                  </Box>)
              }}
            />
            {hasWritePermit &&
              <FunctionField 
                label="Action" 
                sortable={false} 
                align="center"
                render={(rec?: BackupResponseDto & RecPatch) => 
                  rec && isDownloadable(rec) ?
                  <Tooltip title="Config File for Download">
                    <IconButton 
                      sx={{ 
                        color: 'text.primary' as any, 
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                      onClick={() => onDownloadBackupConfigFile(rec?.backupId || 0)}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip> : null}
              />}
          </Datagrid> 
        </Box>
      </Scrollbar>
      {//@ts-ignore
        <DefaultPagination {...listContext} />}
    </Card>
  )
}

export default BackupRecordsTable;

const isDownloadable = (rec: BackupResponseDto) => {
  const downloadable = 
    rec.status  
    ? [BackupStatus.BackingUp, BackupStatus.BackupFailed].indexOf(rec.status) < 0
    : false
  return downloadable
}

const getStatusRelated = (rec: BackupResponseDto) => {
  const status = rec.status || BackupStatus.BackingUp
  const failedVector = [BackupStatus.BackupFailed, BackupStatus.RestoreFailed, BackupStatus.ValidationFailed].map(s => s === status)
  const isFailed = failedVector.some(b => b)
  const isSuccess = BackupStatus.ValidationSuccess === status
  const isUnknown = !isFailed && !isSuccess
  
  const failureTitle = failedVector[0] ? 'Backup'
    : failedVector[1] ? 'Restore'
    : failedVector[2] ? 'Validation'
    : ''
  
  const errLogContent = 
    !isFailed ? undefined
    : ((failedVector[0] ? [rec.backupLog, rec.backupException]
      : failedVector[1] ? [rec.restoreLog, rec.restoreException]
      : failedVector[2] ? [rec.validationLog, rec.validationException] : [])
      .flatMap((s, i) => {
        if (!s) return []
        return [
          '########################',
          `# ${failureTitle.padEnd(10)} ${i === 0 ? 'Log      ' : 'Exception'} #`,
          '########################',
          '',
          s,
          ''
        ]
      }).join('\n')
    )
  const bag = {
    isFailed,
    isSuccess,
    isUnknown,
    color: (isFailed ? 'error.main'
      : isSuccess ? 'success.main'
      : 'action.selected'),
    errLogContent,
  }
  return bag
}
